'use client';

import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getAddresses, getAppointments, getPackages, getProfile } from '@libs/api-proxy-client';
import {
    decodeJwtCookie,
    getSearchStores,
    type IdentityUser,
    PageType,
    type UserData,
    type UserDataToUpdate,
    UserSessionProvider,
    type UserSessionProviderContextProps,
    useDecisions,
    useStoreDispatch,
} from '@libs/common-next';
import {
    ApiFetchResponse,
    AUTH_COOKIES,
    getStoreCookie,
    logger,
    SESSION_EVALUATED_DOM_EVENT,
    stringifyStoreId,
} from '@libs/core';
import { AccountAPI } from '@libs/experience-api';

import { PROFILE_DATA } from '../../constants';
import { useUserSessionLocalStorage } from '../../hooks/useUserSessionLocalStorage';
import { useSyncAuth0IdTokenUser } from './useSyncAuth0IdTokenUser';

interface PageTypeProps {
    pageType?: PageType | null;
}

function decodedUserToken() {
    return decodeJwtCookie<IdentityUser>(AUTH_COOKIES.ACCESS_TOKEN);
}

export default function UserSessionWrapper({ children, pageType }: PropsWithChildren<PageTypeProps>) {
    const isAuth0Enabled = useDecisions('auth0');

    const [user, setUser] = useState<UserSessionProviderContextProps['user']>();

    // In Auth0 mode the mapped id-token identity is written directly into `user`
    // above so it propagates downstream; the hook also clears it on logout.
    const isAuth0IdTokenUserSynced = useSyncAuth0IdTokenUser(isAuth0Enabled, setUser);

    const [userData, setUserData] = useState<UserSessionProviderContextProps['userData']>();
    const [isAuthenticated, setIsAuthenticated] = useState<UserSessionProviderContextProps['isAuthenticated']>();
    const [isSoftAuthenticated, setIsSoftAuthenticated] = useState(false);

    useEffect(() => {
        const syncSessionFromCookies = () => {
            setIsSoftAuthenticated(false);
            // Auth0 mode owns `user` via `useSyncAuth0IdTokenUser`; skip the legacy cookie decode.
            if (isAuth0Enabled) {
                return;
            }
            const decoded = decodedUserToken();
            if (decoded) {
                setUser(prev => (prev?.sid === decoded?.sid ? prev : decoded));
            } else {
                setUser(prev => (prev === undefined ? prev : undefined));
            }
        };
        globalThis.window.addEventListener(SESSION_EVALUATED_DOM_EVENT, syncSessionFromCookies);
        return () => globalThis.window.removeEventListener(SESSION_EVALUATED_DOM_EVENT, syncSessionFromCookies);
    }, [isAuth0Enabled]);

    useEffect(() => {
        if (isAuth0Enabled) {
            return;
        }

        // In legacy mode the cookie JWT is the source of truth. Sync `user` to the
        // decoded cookie, and clear it when no valid cookie decodes so a leftover
        // Auth0-shaped `user` (e.g. after `ff_auth0` flips off mid-session) can't
        // keep `isAuthenticated` active.
        const decoded = decodedUserToken();
        setUser(prev => {
            if (!decoded) {
                return prev === undefined ? prev : undefined;
            }

            return prev?.sid === decoded.sid ? prev : decoded;
        });
    }, [isAuth0Enabled]);

    const [storageUserSession] = useUserSessionLocalStorage({});

    const isCartPage = pageType === PageType.CART;

    const mergeLoyaltyIdFromProfile = useCallback(
        (profile: unknown) => {
            if (!isAuth0Enabled || !profile || typeof profile !== 'object' || profile instanceof Error) {
                return;
            }
            if ('parsedResponse' in profile) {
                return;
            }
            if (!('loyaltyId' in profile)) {
                return;
            }
            const profileLoyaltyId = (profile as { loyaltyId: unknown }).loyaltyId;
            if (profileLoyaltyId !== null && typeof profileLoyaltyId !== 'number') {
                return;
            }
            setUser(prev => {
                if (!prev) {
                    return prev;
                }
                const sessionLoyaltyId = prev.loyaltyid ?? null;
                if (sessionLoyaltyId === profileLoyaltyId) {
                    return prev;
                }
                if (profileLoyaltyId === null) {
                    const { loyaltyid: _, ...userWithoutLoyaltyId } = prev;
                    return userWithoutLoyaltyId as typeof prev;
                }
                return { ...prev, loyaltyid: profileLoyaltyId };
            });
        },
        [isAuth0Enabled]
    );

    const getUserData = useCallback(async () => {
        const [addressesResult, appointmentsResult, packagesResult, paymentsResult, profileResult] =
            await Promise.allSettled(
                isCartPage
                    ? [null, null, null, null, getProfile()]
                    : [getAddresses(), getAppointments(), getPackages(), AccountAPI.Client.getPayments(), getProfile()]
            );

        function getResult<T>(result: PromiseSettledResult<T>) {
            return result.status === 'rejected' ? null : result.value;
        }

        const addresses = getResult(addressesResult);
        const appointments = getResult(appointmentsResult);
        const packages = getResult(packagesResult);
        const payments = getResult(paymentsResult);
        const profile = getResult(profileResult);

        mergeLoyaltyIdFromProfile(profile);

        const newUserData = {
            addresses,
            appointments,
            packages,
            payments,
            profile,
        };

        setUserData(prev => ({
            ...(prev as UserData),
            ...(newUserData as UserData),
        }));
    }, [isCartPage, mergeLoyaltyIdFromProfile, user]);

    /** Rename it to 'updateUserData' when ready to tackle test coverage */
    const updateProfileData = useCallback(
        async (dataToUpdate?: string) => {
            function updateState(key: string, value: ApiFetchResponse<UserDataToUpdate>) {
                // TODO: cover error response
                if (value && !(value instanceof Error || 'parsedResponse' in value)) {
                    setUserData(prev => ({
                        ...(prev as UserData),
                        [key]: value,
                    }));
                }
            }

            switch (dataToUpdate) {
                case PROFILE_DATA.ADDRESSES: {
                    const addresses = await getAddresses();
                    return updateState(PROFILE_DATA.ADDRESSES, addresses);
                }
                case PROFILE_DATA.APPOINTMENTS: {
                    const appointments = await getAppointments();
                    return updateState(PROFILE_DATA.APPOINTMENTS, appointments);
                }
                case PROFILE_DATA.PACKAGES: {
                    const packages = await getPackages();
                    return updateState(PROFILE_DATA.PACKAGES, packages);
                }
                case PROFILE_DATA.PAYMENTS: {
                    const payments = await AccountAPI.Client.getPayments();
                    return updateState(PROFILE_DATA.PAYMENTS, Array.isArray(payments) ? payments : null);
                }
                case PROFILE_DATA.PROFILE: {
                    const profile = await getProfile();
                    mergeLoyaltyIdFromProfile(profile);
                    return updateState(PROFILE_DATA.PROFILE, profile);
                }
                default: {
                    return getUserData();
                }
            }
        },
        [getUserData, mergeLoyaltyIdFromProfile]
    );

    const { updateSelectedStore } = useStoreDispatch();
    useEffect(() => {
        async function initStore() {
            try {
                let storeId = getStoreCookie();
                if (!storeId) {
                    // no store cookie, get closest stores by IP
                    const closestStores = await getSearchStores({});
                    if (closestStores.length > 0) {
                        const storeNumberStr = stringifyStoreId(String(closestStores[0].Store.StoreNumber));
                        if (storeNumberStr) {
                            updateSelectedStore(storeNumberStr);
                            storeId = storeNumberStr;
                        }
                    }
                }
                if (!storeId) {
                    logger.warn('failed to get store id -- both from cookie and search call for closest stores');
                }
            } catch (e) {
                logger.warn('failed store sync:', e);
            }
        }

        initStore();
    }, [updateSelectedStore]);

    useEffect(() => {
        if (user) {
            getUserData();
        }
    }, [user, getUserData]);

    useEffect(() => {
        if (user) {
            setIsAuthenticated('velocity');
        } else {
            setIsAuthenticated(undefined);
        }
    }, [user]);

    const value = useMemo(
        () => ({
            isAuthenticated,
            isAuth0IdTokenUserSynced,
            isSoftAuthenticated,
            setIsSoftAuthenticated,
            setUser,
            storageUserSession,
            user,
            userData,
            updateProfileData,
        }),
        [
            isAuthenticated,
            isAuth0IdTokenUserSynced,
            isSoftAuthenticated,
            setIsSoftAuthenticated,
            storageUserSession,
            user,
            userData,
            updateProfileData,
        ]
    );

    return <UserSessionProvider values={value}>{children}</UserSessionProvider>;
}
